/**
 * The modern replacement for the original `templates` and `header` tables.
 *
 * 2003: font face/size/color rows were concatenated into `<font>` tags and
 *       echoed inline on every page, alongside IE-only scrollbar colors.
 * Now:  the same idea, as CSS custom properties applied to :root. The admin
 *       edits tokens, every component reacts, and nothing renders raw HTML.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';
import { DEFAULT_SKIN } from './skins';
import { applyEffectiveSkin } from './skinPref';

export type ThemeTokens = Record<string, string>;

export const DEFAULT_THEME: ThemeTokens = {
  // The League palette: #121821 base, #425A7E and #6E8AB2 blues, #548334 green.
  // Accent is the light blue (5:1 contrast as text and as a button with dark label text).
  '--color-bg': '#121821',
  '--color-surface': '#19212e',
  '--color-border': '#263448',
  '--color-text': '#e8edf4',
  '--color-muted': '#93a5c0',
  '--color-accent': '#6e8ab2',
  '--color-accent-text': '#121821',
  // "Yes / on / go" green: fills and borders use the base, readable text uses the lighter one.
  '--color-positive': '#548334',
  '--color-positive-text': '#7fb356',
  // Status colors (Server health lights): amber = worth a look, red = act now.
  '--color-warning': '#e6a23c',
  '--color-danger': '#d9534f',
  '--font-body': '"Trebuchet MS", "Segoe UI", Verdana, sans-serif',
  '--font-display': '"Arial Narrow", "Roboto Condensed", "Segoe UI", sans-serif',
  '--radius': '8px',
  // Header menu alignment: flex-start (left) | center | flex-end (right).
  '--nav-justify': 'flex-start',
  // Surface style ("skin") — mapped to a data-skin attribute below. See lib/skins.ts.
  '--skin': DEFAULT_SKIN,
};

interface ThemeValue {
  tokens: ThemeTokens;
  /** Applies immediately for live preview without persisting. */
  preview: (tokens: ThemeTokens) => void;
  save: (tokens: ThemeTokens) => Promise<void>;
  reset: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

function apply(tokens: ThemeTokens) {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(tokens)) {
    // Only set custom properties; a stray key can't inject arbitrary CSS.
    if (key.startsWith('--')) root.style.setProperty(key, value);
  }
  // Resolve the surface-style token into a data-skin attribute the CSS keys off.
  // A member's personal override (localStorage) wins over this org default.
  applyEffectiveSkin(tokens['--skin'] ?? DEFAULT_SKIN);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokens] = useState<ThemeTokens>(DEFAULT_THEME);

  useEffect(() => {
    apply(DEFAULT_THEME);
    api
      .get<{ theme: ThemeTokens }>('/settings/theme')
      .then(({ theme }) => {
        const merged = { ...DEFAULT_THEME, ...theme };
        setTokens(merged);
        apply(merged);
      })
      .catch(() => {
        /* Not configured yet — defaults are already applied. */
      });
  }, []);

  const preview = useCallback((next: ThemeTokens) => {
    setTokens(next);
    apply(next);
  }, []);

  const save = useCallback(async (next: ThemeTokens) => {
    await api.put('/settings/theme', { theme: next });
    setTokens(next);
    apply(next);
  }, []);

  const reset = useCallback(() => {
    setTokens(DEFAULT_THEME);
    apply(DEFAULT_THEME);
  }, []);

  return (
    <ThemeContext.Provider value={{ tokens, preview, save, reset }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
